import moment from 'moment'
import React from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import LegendComponent from '../../common/legend/Legend'
import { i18n } from '../index'
import ChartLegend from './ChartLegend'
import css from './MonthGraph.css'
import type { BudgetPlanItem } from './types'
type GraphType = 'sales' | 'hours' | 'efficiency'
type Props = {
  month: string
  data: Array<BudgetPlanItem>
  graphType: GraphType
  displayCumulativeValues: boolean
}
type DailyData = {
  date: string
  hours: number
  sales: number
  actualSales: number
  actualHours: number
}
type State = {
  resultData: Array<DailyData>
  loading: boolean
}
const STROKES = {
  ACTUAL: '#78A22F',
  BUDGET: '#f06767',
  PLAN: '#F7AC01',
}

class MonthGraph extends React.Component<Props, State> {
  renderWeekAreas = () => {
    const { data } = this.props
    let firstDayOfWeek, lastDayOfWeek, week
    const areas: JSX.Element[] = []
    data.forEach((obj, idx) => {
      const date = obj.date
      const weekOfDate = moment(date).week()

      if (!week) {
        week = weekOfDate
        firstDayOfWeek = date
      } else {
        if (weekOfDate === week) {
          lastDayOfWeek = date
        } else {
          areas.push(
            <ReferenceArea
              key={firstDayOfWeek}
              x1={firstDayOfWeek}
              x2={lastDayOfWeek}
              shape={OffsetShape}
              ifOverflow='extendDomain'
            />,
          )
          week = weekOfDate
          firstDayOfWeek = date
          lastDayOfWeek = undefined
        }
      }

      if (idx === data.length - 1) {
        areas.push(
          <ReferenceArea
            key={firstDayOfWeek}
            x1={firstDayOfWeek}
            x2={lastDayOfWeek}
            shape={OffsetShape}
            ifOverflow='extendDomain'
          />,
        )
      }
    })
    return areas
  }

  render() {
    const { month, graphType, data, displayCumulativeValues } = this.props
    let title, unit, planDataKey, actualDataKey, budgetedDataKey, cumulativeValues, roundFunc
    let legendComponent = <LegendComponent />

    switch (graphType) {
      case 'hours':
        title = 'budgetPlanning.monthlyHours'
        unit = 'h'
        planDataKey = 'plannedHours'
        actualDataKey = 'actualHours'
        budgetedDataKey = 'budgetedHours'

        roundFunc = (val) => parseFloat(val.toFixed(1))

        break

      case 'efficiency':
        title = 'budgetPlanning.monthlyEfficiency'
        unit = ''
        planDataKey = 'plannedEfficiency'
        actualDataKey = 'actualEfficiency'
        budgetedDataKey = 'budgetedEfficiency'

        roundFunc = (val) => Math.round(val)

        break

      default:
        title = 'budgetPlanning.monthlySales'
        unit = '€'
        planDataKey = 'plannedSales'
        actualDataKey = 'actualSales'
        budgetedDataKey = 'budgetedSales'

        roundFunc = (val) => Math.round(val)

        break
    }

    if (displayCumulativeValues) {
      const currentDate = moment().format('YYYY-MM-DD')
      const defaultCumulatives = {
        Budjetti: 0,
        Toteuma: 0,
        Suunnitelma: 0,
      }
      cumulativeValues = data.reduce((prev, cur) => {
        const isSameOrBefore = moment(cur.date).isSameOrBefore(moment(currentDate))
        return isSameOrBefore
          ? {
              Budjetti: roundFunc(prev.Budjetti + cur[budgetedDataKey]),
              Toteuma: roundFunc(prev.Toteuma + cur[actualDataKey]),
              Suunnitelma: roundFunc(prev.Suunnitelma + cur[planDataKey]),
            }
          : prev
      }, defaultCumulatives)
      Object.keys(cumulativeValues).forEach((idx) => (cumulativeValues[idx] += ` ${unit}`))
      legendComponent = <ChartLegend additionalValues={cumulativeValues} />
    }

    return (
      <div>
        <h4>
          {i18n.t(title)}
          &nbsp;
          {moment(month).format('MMMM YYYY')}
        </h4>
        <ResponsiveContainer width='100%' height={300}>
          <ComposedChart
            margin={{
              top: 30,
              right: 10,
              bottom: 10,
              left: 10,
            }}
            data={data}
            className={css.chart}
          >
            <CartesianGrid />

            <XAxis
              dataKey={'date'}
              interval={0}
              tickFormatter={(item) => moment(item).format('D')}
              axisLine={false}
              tickSize={0}
              tickMargin={10}
              className={css.green}
            />
            <YAxis
              padding={{
                top: 0,
              }}
              axisLine={false}
              tickCount={6}
              tickSize={0}
              tickMargin={10}
            >
              <Label value={unit} position='top' offset={15} />
            </YAxis>

            <Tooltip />

            <Legend content={legendComponent} />

            <Line
              type='linear'
              dataKey={planDataKey}
              dot={false}
              unit={unit}
              name={i18n.t('budgetPlanning.plan')?.toString()}
              legendType='line'
              strokeWidth={3}
              stroke={STROKES.PLAN}
            />

            <Line
              type='linear'
              dataKey={budgetedDataKey}
              dot={false}
              unit={unit}
              name={i18n.t('budgetPlanning.budget')?.toString()}
              legendType='line'
              strokeWidth={3}
              stroke={STROKES.BUDGET}
            />

            <Bar
              dataKey={actualDataKey}
              unit={unit}
              name={i18n.t('budgetPlanning.actual')?.toString()}
              barSize={10}
              fill={STROKES.ACTUAL}
            />

            {this.renderWeekAreas()}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    )
  }
} // Default ReferenceAreas would not line up with the charts so needed to make a custom shape with x offset of 17

const OffsetShape = (props) => {
  const newX = props.x + 17
  const y = props.y
  const w = props.width
  const h = props.height
  const d = `M ${newX},${y} h ${w} v ${h} h ${-w} Z`
  return (
    <g>
      <path
        r={props.r}
        fill='#ccc'
        fillOpacity='0.2'
        stroke='none'
        strokeWidth='1'
        radius='0'
        className='recharts-rectangle recharts-reference-area-rect'
        d={d}
      />
    </g>
  )
}

export default MonthGraph
